import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { z } from 'zod';

import { useApi } from '../../../lib/api/api-context.js';
import { parseOrThrow } from '../../../lib/api/contract.js';
import { queryKeys } from '../../../lib/api/query-keys.js';

import {
  createPromotionPayload,
  updatePromotionPayload,
  type PromotionFormInput,
} from './schemas.js';
import { promotionEndpoints } from './endpoints.js';

const createdSchema = z.object({ id: z.string() });

export function useCreatePromotion(): UseMutationResult<
  { readonly id: string },
  unknown,
  PromotionFormInput
> {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values) => {
      const payload = createPromotionPayload(values);
      const answer = await api.post<unknown>(
        promotionEndpoints.create,
        payload,
        crypto.randomUUID(),
      );
      return parseOrThrow(createdSchema, answer, promotionEndpoints.create);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.promotions.lists }),
        queryClient.invalidateQueries({ queryKey: queryKeys.promotions.details }),
      ]);
    },
  });
}

export function useUpdatePromotion(): UseMutationResult<
  void,
  unknown,
  { readonly promotionId: string; readonly values: PromotionFormInput }
> {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ promotionId, values }) => {
      const version = Number(values.version ?? '0');
      const payload = updatePromotionPayload(values, version);
      await api.put<unknown>(promotionEndpoints.update(promotionId), payload);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.promotions.lists }),
        queryClient.invalidateQueries({ queryKey: queryKeys.promotions.details }),
      ]);
    },
  });
}

export function useSoftDeletePromotion(): UseMutationResult<void, unknown, string> {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (promotionId) => {
      await api.delete<unknown>(promotionEndpoints.delete(promotionId));
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.promotions.lists }),
        queryClient.invalidateQueries({ queryKey: queryKeys.promotions.details }),
      ]);
    },
  });
}
